import { Link, usePage } from '@inertiajs/react'
import Navbar from '@/components/layout/Navbar'
import { Button } from '@/components/ui/Button'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types'

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

function submitCheckout(plan: 'monthly' | 'yearly') {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `/subscriptions/checkout?plan=${plan}`
  const csrf = document.createElement('input')
  csrf.type = 'hidden'; csrf.name = '_token'; csrf.value = csrfToken()
  form.appendChild(csrf)
  document.body.appendChild(form)
  form.submit()
}

const FAQS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your billing page — no questions asked. You keep Pro access through your current period.',
  },
  {
    q: 'What counts as a Pro episode?',
    a: "Episodes labeled 'Pro' require an active subscription. All others are free forever.",
  },
  {
    q: 'Can I switch between monthly and yearly?',
    a: 'Yes. Upgrade from monthly to yearly at any time and the remaining days are prorated.',
  },
]

export default function Pricing() {
  const { auth } = usePage<SharedProps>().props
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="py-16 px-4 text-center">
        <p className="text-xs font-semibold tracking-widest text-brand uppercase mb-3">PRICING</p>
        <h1 className="text-5xl font-bold text-gray-900 mb-4">Simple, transparent pricing</h1>
        <p className="text-gray-500 max-w-md mx-auto">
          Start free. Upgrade when you're ready for Pro episodes and progress tracking.
        </p>
      </section>

      {/* Plans */}
      <section className="max-w-4xl mx-auto px-4 grid md:grid-cols-3 gap-6 mb-20">
        {/* Free */}
        <div className="rounded-2xl border border-gray-200 p-8">
          <p className="text-sm font-medium text-gray-500 mb-2">Free</p>
          <p className="text-5xl font-bold text-gray-900 mb-1">$0</p>
          <p className="text-sm text-gray-400 mb-4">/ forever</p>
          <p className="text-sm text-gray-500 mb-6">Start learning with all free screencasts.</p>
          <ul className="space-y-2.5 text-sm mb-8">
            <FeatureItem included>Access to all free episodes</FeatureItem>
            <FeatureItem included>Show Notes with copyable code</FeatureItem>
            <FeatureItem included>Instant full-text search</FeatureItem>
            <FeatureItem included={false}>Pro episodes</FeatureItem>
            <FeatureItem included={false}>Progress tracking</FeatureItem>
          </ul>
          <Link href="/users/sign_up">
            <Button variant="secondary" className="w-full rounded-xl">Get started free</Button>
          </Link>
        </div>

        {/* Pro Monthly */}
        <div className="rounded-2xl border-2 border-brand p-8 relative">
          <span className="absolute top-4 left-6 text-xs font-semibold text-brand bg-rose-50 px-2 py-0.5 rounded-full">
            MOST POPULAR
          </span>
          <p className="text-sm font-semibold text-brand mt-4 mb-2">Pro Monthly</p>
          <p className="text-5xl font-bold text-gray-900 mb-1">$9</p>
          <p className="text-sm text-gray-400 mb-4">/ month</p>
          <p className="text-sm text-gray-500 mb-6">Full access billed monthly. Cancel anytime.</p>
          <ul className="space-y-2.5 text-sm mb-8">
            <FeatureItem included>Everything in Free</FeatureItem>
            <FeatureItem included>All Pro episodes unlocked</FeatureItem>
            <FeatureItem included>Progress tracking & bookmarks</FeatureItem>
            <FeatureItem included>Watch history & learning stats</FeatureItem>
          </ul>
          {auth.user ? (
            <Button className="w-full rounded-xl" onClick={() => submitCheckout('monthly')}>Start Pro Monthly</Button>
          ) : (
            <Link href="/users/sign_up"><Button className="w-full rounded-xl">Start Pro Monthly</Button></Link>
          )}
        </div>

        {/* Pro Yearly */}
        <div className="rounded-2xl border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Pro Yearly</p>
            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Save $29</span>
          </div>
          <p className="text-5xl font-bold text-gray-900 mb-1">$79</p>
          <p className="text-sm text-gray-400 mb-4">/ year</p>
          <p className="text-sm text-gray-500 mb-6">Best value for serious learners.</p>
          <ul className="space-y-2.5 text-sm mb-8">
            <FeatureItem included>Everything in Pro Monthly</FeatureItem>
            <FeatureItem included>Save 27% vs monthly billing</FeatureItem>
            <FeatureItem included>Early access to new series</FeatureItem>
            <FeatureItem included>Priority support</FeatureItem>
          </ul>
          {auth.user ? (
            <Button variant="dark" className="w-full rounded-xl" onClick={() => submitCheckout('yearly')}>Start Pro Yearly</Button>
          ) : (
            <Link href="/users/sign_up"><Button variant="dark" className="w-full rounded-xl">Start Pro Yearly</Button></Link>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-4 pb-20">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Common questions</h2>
        <div className="divide-y divide-gray-100">
          {FAQS.map((faq) => (
            <div key={faq.q} className="py-5">
              <p className="font-semibold text-gray-900 mb-1">{faq.q}</p>
              <p className="text-sm text-gray-500">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function FeatureItem({ included, children }: { included: boolean; children: React.ReactNode }) {
  return (
    <li className={cn('flex items-center gap-2', included ? 'text-gray-700' : 'text-gray-300')}>
      {included ? (
        <Check className="w-4 h-4 text-green-500 shrink-0" />
      ) : (
        <X className="w-4 h-4 text-gray-300 shrink-0" />
      )}
      {children}
    </li>
  )
}
